import { DELETION_FLAG, parse } from "@packages/core";
import { useForm } from "@tanstack/react-form";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { sum } from "es-toolkit";
import React, { useEffect, useMemo, useState } from "react";
import { LuImport, LuPencil, LuPlus, LuSearch, LuSearchX } from "react-icons/lu";
import { Merge } from "type-fest";
import * as v from "valibot";

import {
  ButtonStyle,
  DropDownList,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PageNumberController,
  PageSizeSelector,
  ResidentTable,
  TypographyStyle,
  showError,
} from "#components";
import {
  useFacilityQuery,
  useGetErrorMessage,
  useResidentInfiniteQuery,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/residents/")({
  component: Component,
  validateSearch: v.object({
    agreed: v.fallback(v.optional(v.boolean()), undefined),
    deleted: v.fallback(
      v.optional(v.picklist([DELETION_FLAG.DEFAULT, DELETION_FLAG.ARCHIVED])),
      undefined,
    ),
    name: v.fallback(v.optional(v.string()), undefined),
  }),
});

function Component() {
  const { facilityKey } = Route.useParams();
  const { agreed, deleted = DELETION_FLAG.DEFAULT, name } = Route.useSearch();

  const facilityQuery = useFacilityQuery(facilityKey);
  const { data: facility } = facilityQuery;
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // const { fetchNextPage, loading, pages } = useListedResidents(
  //   { deleted, facilityKey },
  //   { pageSize: deleted !== DELETION_FLAG.DEFAULT ? pageSize : undefined },
  // );
  const residentInfiniteQuery = useResidentInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", deleted],
    ],
    limit: deleted !== DELETION_FLAG.DEFAULT ? pageSize : undefined,
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (
        deleted === DELETION_FLAG.DEFAULT &&
        residentInfiniteQuery.hasNextPage &&
        !residentInfiniteQuery.isFetching
      ) {
        residentInfiniteQuery.fetchNextPage();
      }
    },
    [deleted, residentInfiniteQuery],
  );

  const numberOfResidents = useMemo(
    function () {
      return sum(
        residentInfiniteQuery.data?.pages.map(function (page) {
          return page.residents.length;
        }) ?? [],
      );
    },
    [residentInfiniteQuery.data?.pages],
  );

  const residents = useMemo(
    function () {
      if (deleted === DELETION_FLAG.DEFAULT) {
        return residentInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.residents;
          })
          .filter(function (resident) {
            return (
              resident.name.includes(name ?? "") &&
              (agreed === undefined || agreed === Boolean(resident.agreed))
            );
          });
      } else {
        return residentInfiniteQuery.data?.pages.at(pageNumber)?.residents;
      }
    },
    [agreed, deleted, name, pageNumber, residentInfiniteQuery.data],
  );

  return (
    <main
      className={css({
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
      data-maximized
    >
      <PageName>
        {facility
          ? translate("%ResidentManagement.0%") + " - " + facility.name
          : translate("%Loading.0%")}
      </PageName>
      <p className={TypographyStyle({ ellipsis: true, fontSize: "24px", fontWeight: 700 })}>
        {translate("%ResidentManagement.0%")}
      </p>

      <div
        className={css({
          alignItems: { smDown: "center", sm: "flex-end" },
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
        })}
      >
        <div
          className={css({
            alignItems: "center",
            display: { smDown: "flex", sm: "none" },
            gap: "16px",
          })}
        >
          <button
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "6px",
              color: "white",
              icon: true,
              padding: "12px",
            })}
            onClick={function () {
              setOpen(function (open) {
                return !open;
              });
            }}
          >
            {open ? (
              <LuSearchX className={css({ height: "12px", width: "auto" })} />
            ) : (
              <LuSearch className={css({ height: "12px", width: "auto" })} />
            )}
          </button>
        </div>

        <div
          className={css({
            display: "flex",
            gap: "16px",
            justifySelf: "end",
            minWidth: 0,
            order: 1,
          })}
        >
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px", minWidth: 0 })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents/registration"
          >
            <LuPlus className={css({ flexShrink: 0, height: "14px", width: "auto" })} />
            <span className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}>
              {translate("%Add.1%")}
            </span>
          </Link>
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px", minWidth: 0 })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents/batch-registration"
          >
            <LuImport className={css({ flexShrink: 0, height: "14px", width: "auto" })} />
            <span className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}>
              {translate("%Add.2%")}
            </span>
          </Link>
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px", minWidth: 0 })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/simple-independence-levels/registration"
          >
            <LuPencil className={css({ flexShrink: 0, height: "14px", width: "auto" })} />
            <span className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}>
              {translate("%AssessmentInitialization.0%")}
            </span>
          </Link>
        </div>

        <SearchForm className={css({ order: { smDown: 2, sm: 0 } })} open={open} />
      </div>

      <div
        className={cx(
          css({ flexGrow: 1, marginTop: "8px", overflowX: "auto", width: "100%" }),
          deleted === DELETION_FLAG.DEFAULT && numberOfResidents === 0 && css({ display: "none" }),
        )}
      >
        <ResidentTable
          action={function (resident) {
            return (
              <Link
                className={ButtonStyle({
                  appearance: "outlined",
                  borderRadius: "4px",
                  fontSize: "14px",
                  padding: "10px 16px",
                  color: "primary",
                })}
                params={{ facilityKey, userKey: resident.userKey }}
                to="/facilities/$facilityKey/residents/$userKey/modification"
              >
                {translate("%Details.0%")}
              </Link>
            );
          }}
          className={css({ height: "100%" })}
          loading={residentInfiniteQuery.isFetching}
          rows={residents}
        />
      </div>

      <div
        className={cx(
          css({
            alignItems: "flex-start",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "space-between",
            marginTop: "8px",
            overflow: "clip",
          }),
          deleted === DELETION_FLAG.DEFAULT && css({ display: "none" }),
        )}
      >
        <PageNumberController
          loading={residentInfiniteQuery.isFetching}
          next={
            (residentInfiniteQuery.data &&
              pageNumber + 1 < residentInfiniteQuery.data.pages.length) ||
            residentInfiniteQuery.hasNextPage
          }
          onNext={function (value) {
            residentInfiniteQuery.fetchNextPage().then(function () {
              setPageNumber(value);
            });
          }}
          onPrevious={function (value) {
            setPageNumber(value);
          }}
          value={pageNumber}
        />
        <PageSizeSelector
          onChange={function (value) {
            setPageSize(value);
          }}
          options={[25, 50, 100]}
          value={pageSize}
        />
      </div>

      <div
        className={css({
          display: "flex",
          flexGrow: 1,
          alignItems: "center",
          flexDirection: "column",
          justifyContent: "center",
          marginTop: "16px",
          overflowX: "auto",
          width: "100%",

          '&[data-display-none="true"]': {
            display: "none",
          },
        })}
        data-display-none={deleted !== DELETION_FLAG.DEFAULT || numberOfResidents > 0}
      >
        <div
          className={css({
            alignItems: { smDown: "flex-start", sm: "center" },
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
          })}
        >
          <p className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}>
            {translate("%NoResidents.0%")}
          </p>
          <p
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "14px",
              lineHeight: 1,
              marginTop: "6px",
            })}
          >
            {translate("%NoResidents.1%")}
          </p>
        </div>
        <div
          className={css({
            alignItems: "center",
            display: "flex",
            flexDirection: { smDown: "column", sm: "row" },
            gap: "12px",
            justifyContent: "center",
            marginTop: "12px",
            width: "100%",
          })}
        >
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "contained",
                borderRadius: "4px",
                color: "primary",
                gap: "8px",
                fontSize: "14px",
                padding: "10px 16px",
              }),
              { width: { smDown: "stretch", sm: "auto" } },
            )}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents/registration"
          >
            <LuPlus />
            <span>{translate("%Add.1%")}</span>
          </Link>

          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "4px",
                color: "outlined",
                gap: "8px",
                fontSize: "14px",
                padding: "10px 16px",
              }),
              { width: { smDown: "stretch", sm: "auto" } },
            )}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents/batch-registration"
          >
            <LuImport />
            <span>{translate("%Add.2%")}</span>
          </Link>
        </div>
      </div>
    </main>
  );
}

type SearchFormProperties = Merge<
  React.ComponentProps<"form">,
  {
    open?: boolean;
  }
>;

function SearchForm({ className, open, ...properties }: SearchFormProperties) {
  const { agreed, deleted = DELETION_FLAG.DEFAULT, name } = Route.useSearch();

  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();

  const schema = useMemo(function () {
    return v.object({
      agreed: v.nullable(v.boolean()),
      deleted: v.picklist([DELETION_FLAG.DEFAULT, DELETION_FLAG.ARCHIVED]),
      name: v.string(),
    });
  }, []);
  const defaultValues = useMemo(
    function (): v.InferInput<typeof schema> {
      return {
        agreed: agreed ?? null,
        deleted: deleted ?? DELETION_FLAG.DEFAULT,
        name: name ?? "",
      };
    },
    [agreed, deleted, name],
  );

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      try {
        const output = parse(schema, input);
        console.debug("[onSubmit]", new Date(), output, input);
        navigate({
          from: "/facilities/$facilityKey/residents/",
          replace: true,
          search: {
            agreed: output.agreed ?? undefined,
            name: output.name || undefined,
            deleted: output.deleted || undefined,
          },
        });
      } catch (error) {
        showError(error);
      }
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: schema },
  });

  return (
    <form
      {...properties}
      className={cx(
        css({
          flexDirection: { smDown: "column", sm: "row" },
          gap: { smDown: "12px", sm: "16px" },
        }),
        open ? css({ display: "flex" }) : css({ display: { smDown: "none", sm: "flex" } }),
        className,
      )}
      data-main
      onSubmit={function (event) {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.Field name="deleted" validators={{ onChange: schema.entries.deleted }}>
        {function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({
                maxWidth: { smDown: "stretch", sm: "max-content" },
                width: "stretch",
              })}
              invalid={field.state.meta.errors.length > 0}
              label={translate("%DeletionFlag.0%")}
            >
              <DropDownList
                className={css({ backgroundColor: "white", width: "stretch" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(Number.parseInt(event.target.value) as any);
                  form.handleSubmit();
                }}
                value={field.state.value ?? DELETION_FLAG.DEFAULT}
              >
                {[DELETION_FLAG.DEFAULT, DELETION_FLAG.ARCHIVED].map(function (deletionFlag) {
                  return (
                    <option key={deletionFlag} value={deletionFlag}>
                      {translate("%DeletionFlag." + deletionFlag + ".0%")}
                    </option>
                  );
                })}
              </DropDownList>
            </FormField>
          );
        }}
      </form.Field>

      <form.Field name="name" validators={{ onChange: schema.entries.name }}>
        {function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({
                maxWidth: { smDown: "stretch", sm: "240px" },
                width: "stretch",
                '&[data-display-none="true"]': { display: "none" },
              })}
              data-display-none={deleted !== DELETION_FLAG.DEFAULT}
              invalid={field.state.meta.errors.length > 0}
              label={translate("%ResidentName.0%")}
            >
              <FormFieldInput
                className={css(FormFieldInputStyle.raw({ backgroundColor: "white" }), {
                  width: "100%",
                })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormField>
          );
        }}
      </form.Field>

      <form.Field name="agreed" validators={{ onChange: schema.entries.agreed }}>
        {function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({
                maxWidth: { smDown: "stretch", sm: "max-content" },
                width: "stretch",
                '&[data-display-none="true"]': { display: "none" },
              })}
              data-display-none={deleted !== DELETION_FLAG.DEFAULT}
              invalid={field.state.meta.errors.length > 0}
              label={translate("%PrivacyPolicy.2%")}
            >
              <DropDownList
                className={css({ backgroundColor: "white", width: "stretch" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(
                    event.target.value === "true"
                      ? true
                      : event.target.value === "false"
                        ? false
                        : null,
                  );
                  form.handleSubmit();
                }}
                value={String(field.state.value)}
              >
                <option value={String(undefined)}>{translate("%All.0%")}</option>
                {[true, false].map(function (agreed) {
                  return (
                    <option key={String(agreed)} value={String(agreed)}>
                      {translate("%PrivacyPolicy." + agreed + ".0%")}
                    </option>
                  );
                })}
              </DropDownList>
            </FormField>
          );
        }}
      </form.Field>
    </form>
  );
}
